import {
  ModToggleButton,
  ModToggleButtonGroup,
  ModTooltip
} from '@moderneinc/design-system-components';
import { iconSize } from '@moderneinc/design-system-tokens';
import { Box, Stack, type SxProps, type Theme } from '@mui/material';
import type { ComponentType, FunctionComponent } from 'react';
import { MonitorIcon, MoonIcon, SunnyIcon } from '../../icons/icons';
import {
  type ThemePreference,
  useUserPreferenceStore
} from '../../stores/user-preference.store';

type ThemeOption = {
  value: ThemePreference;
  label: string;
  /** Tooltip / aria-label; falls back to `label`. */
  tooltip?: string;
  Icon: ComponentType<{ size?: number }>;
};

const THEME_OPTIONS: ThemeOption[] = [
  { value: 'light', label: 'Light', Icon: SunnyIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
  {
    value: 'system',
    label: 'System',
    tooltip: 'Sync with system',
    Icon: MonitorIcon
  }
];

type ThemeToggleProps = {
  /**
   * Show a text label beside each icon (settings page). Default is icon-only
   * with tooltips — compact, for menus.
   */
  showLabels?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * Persisted light/dark/system theme control. Writes `themePreference` straight
 * to the store so the theme applies live and both mount points (account menu +
 * Appearance settings) stay in sync. Shared so they can't drift.
 */
export const ThemeToggle: FunctionComponent<ThemeToggleProps> = ({
  showLabels = false,
  sx
}) => {
  const themePreference = useUserPreferenceStore((s) => s.themePreference);
  const update = useUserPreferenceStore((s) => s.update);

  return (
    <ModToggleButtonGroup
      exclusive
      size="small"
      variant="segmented"
      value={themePreference}
      onChange={(_event, next: ThemePreference | null) => {
        if (next) {
          update({ themePreference: next });
        }
      }}
      aria-label="Theme"
      sx={sx}
    >
      {THEME_OPTIONS.map(({ value, label, tooltip, Icon }) => (
        <ModToggleButton
          key={value}
          value={value}
          aria-label={tooltip ?? label}
        >
          {showLabels ? (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
              <Icon size={iconSize.md} /> {label}
            </Stack>
          ) : (
            <ModTooltip title={tooltip ?? label} placement="top">
              <Box sx={{ display: 'flex' }}>
                <Icon size={iconSize.md} />
              </Box>
            </ModTooltip>
          )}
        </ModToggleButton>
      ))}
    </ModToggleButtonGroup>
  );
};
